/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { get, set, includes } from 'lodash';
import React, { useMemo, useEffect, useState } from 'react';
import { Form, Grid, Row, Col } from 'react-bootstrap/lib';
import { DealerType } from '@upstart/harmony/lib/legos/constants';
import { getWebsiteDealerContext } from '@upstart/harmony/lib/website-utils';
import { Feature, useGrowthBook } from 'hooks/GrowthBook';
import DealerService from '../../../../services/DealerService';
import Switch from '../inputs/Switch';
import TextInput from '../inputs/TextInput';
import NumberInput from '../inputs/NumberInput';
import ColorPicker from '../inputs/ColorPicker';
import CodeTextArea from '../inputs/CodeTextArea';
import ImageUpload from '../../../WidgetConfigEditor/inputs/ImageUpload';
import {
  STANDALONE_TRADEIN_BUTTON_CLASSNAME,
  STANDALONE_PREAPPROVAL_BUTTON_CLASSNAME,
  STANDALONE_BROWSE_BUTTON_CLASSNAME,
  STANDALONE_BYO_BUTTON_CLASSNAME,
  STANDALONE_BYO_BANNER_CLASSNAME,
  RESERVATION_KIA_BUTTON_CLASSNAME,
  RESERVATION_KIA_BANNER_CLASSNAME,
  RESERVATION_SUBARU_BUTTON_CLASSNAME,
  RESERVATION_SUBARU_BANNER_CLASSNAME,
  widgetsScriptURL,
  ddcWidgetsAdapterURL,
} from '../../../../util/constants';

const STANDALONE_BUTTON_LIST = [
  {
    buttonType: 'tradeInButton',
    title: 'Standalone TradeIn Button',
    placeholder: 'Value Your Trade',
    containerClassName: STANDALONE_TRADEIN_BUTTON_CLASSNAME,
  },
  {
    buttonType: 'preApprovalButton',
    title: 'Standalone Pre-Approved Button',
    placeholder: 'Get Pre-Approved',
    containerClassName: STANDALONE_PREAPPROVAL_BUTTON_CLASSNAME,
  },
  {
    buttonType: 'browseButton',
    title: 'Standalone Browse Button',
    placeholder: 'Click here to Unlock Best Pricing!',
    placeholderUnlocked: 'Browse all vehicles!',
    containerClassName: STANDALONE_BROWSE_BUTTON_CLASSNAME,
  },
];

const BYO_BUTTON = {
  buttonType: 'byoButton',
  title: 'Standalone Build Your Own CTA',
  placeholder: 'Build & Price!',
  containerClassName: STANDALONE_BYO_BUTTON_CLASSNAME,
};

const BYO_BANNER = {
  buttonType: 'byoBanner',
  title: 'Standalone BYO Banner',
  placeholder: 'Build & Price!',
  containerClassName: STANDALONE_BYO_BANNER_CLASSNAME,
};

const KIA_BUTTON = {
  buttonType: 'kiaButton',
  title: 'Kia Reservations CTA',
  placeholder: 'Build & Price!',
  containerClassName: RESERVATION_KIA_BUTTON_CLASSNAME,
};

const KIA_BANNER = {
  buttonType: 'kiaBanner',
  title: 'Kia Reservations Banner',
  placeholder: 'Build & Price!',
  containerClassName: RESERVATION_KIA_BANNER_CLASSNAME,
};

const SUBARU_RESERVATION_BUTTON = {
  buttonType: 'subaruReservationButton',
  title: 'Subaru Reservations CTA',
  placeholder: 'Reserve Your Subaru Now!',
  containerClassName: RESERVATION_SUBARU_BUTTON_CLASSNAME,
};

const SUBARU_RESERVATION_BANNER = {
  buttonType: 'subaruReservationBanner',
  title: 'Subaru Reservations Banner',
  placeholder: 'Reserve Your Subaru Now!',
  containerClassName: RESERVATION_SUBARU_BANNER_CLASSNAME,
};
function StandaloneButtonsEditor(props) {
  const { website, onChange, onSave, formData } = props;
  const growthBook = useGrowthBook();
  const isSubaruReservationEnabled = growthBook.getFeatureValue(
    Feature.SUBARU_RESERVATION_ENABLED,
    false,
  );

  const onChangeHandler = e => {
    set(formData, e.target.name, e.target.value);
    onChange(formData);
  };

  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    onSave();
  };

  const [websiteDealerContext, setWebsiteDealerContext] = useState(
    getWebsiteDealerContext(website),
  );

  useEffect(() => {
    if (!websiteDealerContext) {
      DealerService.getDealer(website?.dealer_id).then(Dealer => {
        !!Dealer && setWebsiteDealerContext(Dealer);
      });
    }
  }, [website]);

  const byoEnabled = includes(
    [DealerType.BYO_ONLY, DealerType.BYO_ENABLED],
    websiteDealerContext?.dealerType,
  );

  const kiaReservationEnabled =
    websiteDealerContext?.featureConfig?.kiaReservation?.enabled || false;

  const buttonsToRender = useMemo(() => {
    let BUTTONS_LIST = [...STANDALONE_BUTTON_LIST];
    if (byoEnabled) {
      BUTTONS_LIST = [...BUTTONS_LIST, BYO_BANNER, BYO_BUTTON];
    }
    if (kiaReservationEnabled) {
      BUTTONS_LIST = [...BUTTONS_LIST, KIA_BANNER, KIA_BUTTON];
    }
    if (isSubaruReservationEnabled) {
      BUTTONS_LIST = [
        ...BUTTONS_LIST,
        SUBARU_RESERVATION_BANNER,
        SUBARU_RESERVATION_BUTTON,
      ];
    }
    return BUTTONS_LIST;
  }, [
    byoEnabled,
    kiaReservationEnabled,
    isSubaruReservationEnabled,
    websiteDealerContext,
  ]);

  const renderStandaloneButtonSection = ({
    title,
    buttonType,
    placeholder,
    containerClassName,
    placeholderUnlocked,
  }) => {
    const isBYOBanner = buttonType === BYO_BANNER.buttonType;
    return (
      <Col sm={6}>
        <h4>{title}</h4>
        <Switch
          name={`${buttonType}.enabled`}
          formData={formData}
          onChange={onChangeHandler}
        />
        {get(formData, 'useCTAImages') && (
          <>
            <ImageUpload
              name={`${buttonType}.image`}
              label={isBYOBanner ? 'Desktop Button Image' : 'Button Image'}
              formData={formData}
              onChange={onChangeHandler}
            />
            {!!isBYOBanner && (
              <ImageUpload
                name={`${buttonType}.mobileImage`}
                label="Mobile Button Image"
                formData={formData}
                onChange={onChangeHandler}
              />
            )}
          </>
        )}
        <TextInput
          name={`${buttonType}.text`}
          label="Button Text"
          formData={formData}
          onChange={onChangeHandler}
          placeholder={placeholder}
        />
        {buttonType === 'browseButton' && (
          <TextInput
            name={`${buttonType}.textUnlocked`}
            label="Button Unlocked Text"
            formData={formData}
            onChange={onChangeHandler}
            placeholder={placeholderUnlocked}
          />
        )}
        <NumberInput
          name={`${buttonType}.textSize`}
          label="Button Text Size"
          formData={formData}
          onChange={onChangeHandler}
          info="Enter value in pixels"
        />
        <ColorPicker
          name={`${buttonType}.backgroundColor`}
          label="Button Background Color"
          formData={formData}
          onChange={onChangeHandler}
        />
        <ColorPicker
          name={`${buttonType}.textColor`}
          label="Button Text Color"
          formData={formData}
          onChange={onChangeHandler}
        />
        <CodeTextArea
          label="Button Container"
          value={`<!— Copy this markup into your page template where you want the button to render —>\n<div class="${containerClassName}"></div>`}
        />
      </Col>
    );
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Grid fluid>
        <Row>
          <Col sm={6}>
            <div className="widget-text-toggle-group">
              <Switch
                name="useCTAImages"
                label="CTA Images Instead of Text"
                formData={formData}
                onChange={onChangeHandler}
              />
            </div>
          </Col>
        </Row>
        <Row>{buttonsToRender.map(renderStandaloneButtonSection)}</Row>
        <Row>
          <Col sm={12}>
            <h4>Standard Integration</h4>
            <small>
              Copy the markup below into your page template to render these
              buttons. Only add this script once per page.
            </small>
            <CodeTextArea
              label="Agent Script"
              value={`<!— Copy this markup into your page template after the closing <body/> tag —>\n<script src="${widgetsScriptURL}" type="text/javascript" id="prodigyButtonGroupAgent" websiteId="${website.id}"></script>`}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <h4>Dealer Dot Com Integration</h4>
            <small>Provide the following script to DDC support.</small>
            <CodeTextArea
              label="DDC Adapter Script"
              value={`<!— Copy this markup into your page template after the closing <body/> tag —>\n<script src="${ddcWidgetsAdapterURL}" type="text/javascript></script>`}
            />
            <small>
              Additionally provide the following websiteId to DDC support.
            </small>
            <CodeTextArea
              label="Website Id"
              value={`websiteId = ${website.id}`}
            />
          </Col>
        </Row>
      </Grid>
    </Form>
  );
}

StandaloneButtonsEditor.propTypes = {
  website: PropTypes.object,
  formData: PropTypes.object,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
};

export default StandaloneButtonsEditor;
